import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/crmApi";

/**
 * Récupère `path` immédiatement puis en arrière-plan à intervalle régulier.
 * - Mise à jour silencieuse : les données précédentes restent affichées (pas de loader plein écran).
 * - Se met en pause quand l'onglet est masqué, et se resynchronise au retour.
 */
export function useLiveData(path, { interval = 15000, enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const pathRef = useRef(path);
  pathRef.current = path;

  const fetchNow = useCallback(async (silent) => {
    if (silent) setRefreshing(true);
    try {
      const res = await api.get(pathRef.current);
      setData(res.data);
      setLastUpdated(new Date());
    } catch {
      /* on garde les données précédentes en cas d'erreur réseau ponctuelle */
    } finally {
      if (silent) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    fetchNow(false);
    const timer = setInterval(() => { if (!document.hidden) fetchNow(true); }, interval);
    const onVisible = () => { if (!document.hidden) fetchNow(true); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); };
  }, [path, interval, enabled, fetchNow]);

  return { data, refreshing, lastUpdated, refresh: () => fetchNow(true) };
}
